import React, {useEffect, useState} from 'react';
import {UserUpdateDto} from "../../../../common/apis/user/UserUpdateDto";
import userService from "../../../../common/apis/user/UserApiClient";
import Dialog from '../../../../components/dialogs/Dialog';
import DialogTitle from '../../../../components/dialogs/DialogTitle';
import DialogContent from '../../../../components/dialogs/DialogContent';
import DialogActions from '../../../../components/dialogs/DialogActions';
import TextField from '../../../../components/fields/textfield/TextField';
import Grid from '../../../../components/dialogs/Grid';
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../components/button/ButtonLayout";
import {SaveButton} from "../../../../components/button/SaveButton";
import {CancelButton} from "../../../../components/button/CancelButton";
import {t} from '../../../../store/localization/TranslMessasge';
import {UserDto} from "../../../../common/apis/user/UserDto";
import {UserValidator} from "./UserValidation";
import {UserErrorCode} from "./UserErrorCode";
import {UsersTranslationKey} from "./UsersTranslationKey";
import {CommonTranslationKey} from "../CommonTranslationKey";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import {ValidationError} from "../../../../common/ValidationError";
import Snackbar from "../snackbar/Snackbar";

interface UserBasicInfoFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    description: string;
}

interface UserBasicInfoUpdateDialogProps {
    open: boolean;
    user: UserDto;
    userValidator: UserValidator;
    onClose: () => void;
    onUpdated: () => void;
}

export default function UserBasicInfoUpdateDialog({open, user, userValidator, onClose, onUpdated}: UserBasicInfoUpdateDialogProps) {
    const [formData, setFormData] = useState<UserBasicInfoFormData>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        description: ''
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [inProgress, setInProgress] = useState(false);

    useEffect(() => {
        if (open) {
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                phone: user.phone || '',
                description: user.description || ''
            });
            setErrors({});
        }
    }, [open, user]);

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};

        if (userValidator.isEmailError(formData.email.trim())) {
            newErrors.email = t(UsersTranslationKey.USER_EMAIL_INVALID);
        }

        if (userValidator.isFirstnameError(formData.firstName)) {
            newErrors.firstName = t(CommonTranslationKey.FIELD_TOO_LONG);
        }

        if (userValidator.isLastnameError(formData.lastName)) {
            newErrors.lastName = t(CommonTranslationKey.FIELD_TOO_LONG);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    function save() {
        if (!validateForm()) {
            return;
        }

        setInProgress(true);

        const userUpdate: UserUpdateDto = {
            firstName: formData.firstName.trim() || undefined,
            lastName: formData.lastName.trim() || undefined,
            email: formData.email.trim(),
            phone: formData.phone.trim() || undefined,
            description: formData.description.trim() || undefined
        };

        userService.updateUser(user.login, userUpdate)
                .then(() => {
                    onUpdated();
                    onClose();
                    Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                })
                .catch((error) => {
                    if (error instanceof ValidationError && error.error.code === UserErrorCode.EMAIL_ALREADY_IN_USE) {
                        setErrors({email: t(UsersTranslationKey.USER_EMAIL_ALREADY_IN_USE)});
                    } else {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
                    }
                })
                .finally(() => setInProgress(false));
    }

    return (
            <Dialog open={open} onClose={onClose} maxWidth="md">
                <DialogTitle title={t(UsersTranslationKey.EDIT_DETAILS)}
                        onDialogClosed={onClose}/>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                    label={t(UsersTranslationKey.USER_FIRST_NAME)}
                                    value={formData.firstName}
                                    onChange={value => setFormData({...formData, firstName: value})}
                                    errorMessage={errors.firstName}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                    label={t(UsersTranslationKey.USER_LAST_NAME)}
                                    value={formData.lastName}
                                    onChange={value => setFormData({...formData, lastName: value})}
                                    errorMessage={errors.lastName}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                    label={t(UsersTranslationKey.USER_EMAIL)}
                                    value={formData.email}
                                    onChange={value => setFormData({...formData, email: value})}
                                    required={true}
                                    errorMessage={errors.email}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                    label={t(UsersTranslationKey.USER_PHONE)}
                                    value={formData.phone}
                                    onChange={value => setFormData({...formData, phone: value})}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                    label={t(UsersTranslationKey.NOTE)}
                                    value={formData.description}
                                    onChange={value => setFormData({...formData, description: value})}
                                    multiline
                                    rows={4}/>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonLayout>
                        <PositiveButtons>
                            <SaveButton onClick={save} disabled={inProgress}/>
                        </PositiveButtons>
                        <NegativeButtons>
                            <CancelButton onClick={onClose} disabled={inProgress}/>
                        </NegativeButtons>
                    </ButtonLayout>
                </DialogActions>
            </Dialog>
    );
}
